import React, { useState, useEffect, useRef } from 'react';

interface EntraConsentPromptProps {
  customerName: string;
  onClose: () => void;
}

/**
 * Component to prompt the user to consent to Microsoft Entra admin consent
 * Uses a popup window approach for better user experience
 */
const EntraConsentPrompt: React.FC<EntraConsentPromptProps> = ({ customerName, onClose }) => {
  const [consentStatus, setConsentStatus] = useState<'initial' | 'pending' | 'success' | 'error'>('initial');
  const popupRef = useRef<Window | null>(null);
  const checkIntervalRef = useRef<number | null>(null);
  
  // Client ID for the application
  const clientId = '621f0922-c148-455b-8771-b9fec9a69b64';
  
  // Build the consent URL with the redirect URI
  // Note: This redirect URI must be registered in the Azure AD app registration
  const redirectUri = encodeURIComponent(`${window.location.origin}/auth/admin-consent-callback`);
  const consentUrl = `https://login.microsoftonline.com/common/adminconsent?client_id=${clientId}&redirect_uri=${redirectUri}`;
  
  // Clean up interval on unmount
  useEffect(() => {
    return () => {
      if (checkIntervalRef.current) {
        window.clearInterval(checkIntervalRef.current);
      }
    };
  }, []);
  
  // Handle opening the consent popup
  const handleOpenConsent = () => {
    // Calculate center position for the popup
    const width = 800;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    
    // Open popup
    popupRef.current = window.open(
      consentUrl,
      'EntraConsentWindow',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
    
    // Set status to pending
    setConsentStatus('pending');
    
    // Check if popup was blocked
    if (!popupRef.current || popupRef.current.closed) {
      setConsentStatus('error');
      return;
    }
    
    // Monitor popup closure
    checkIntervalRef.current = window.setInterval(() => {
      if (popupRef.current && popupRef.current.closed) {
        // Popup was closed, assume success
        // In a production app, you would verify this with an API call
        setConsentStatus('success');
        
        // Clean up interval
        if (checkIntervalRef.current) {
          window.clearInterval(checkIntervalRef.current);
          checkIntervalRef.current = null;
        }
      }
    }, 1000);
  };
  
  // If consent was successful, show a success message
  if (consentStatus === 'success') {
    return (
      <div className="card p-lg">
        <h2 className="text-success mb-md">Admin Consent Provided</h2>
        
        <div className="mb-lg">
          <p className="mb-md">
            Thank you for providing admin consent for <strong>{customerName}</strong>.
          </p>
          <p className="mb-md">
            You can now proceed with setting up the customer in the LMS Manager.
          </p>
          <p className="mb-md">
            The next step is to prep the tenant by clicking the "Prep Tenant" button on the customer list.
          </p>
        </div>
        
        <div className="flex justify-end">
          <button 
            className="btn btn-primary"
            onClick={onClose}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }
  
  // If there was an error, show an error message
  if (consentStatus === 'error') {
    return (
      <div className="card p-lg">
        <h2 className="text-error mb-md">Consent Error</h2>
        
        <div className="mb-lg">
          <p className="mb-md">
            There was an error opening the consent window. This could be due to popup blocking or another browser issue.
          </p>
          <p className="mb-md">
            Please ensure popups are allowed for this site and try again.
          </p>
        </div>
        
        <div className="flex justify-between">
          <button 
            className="btn btn-secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => {
              setConsentStatus('initial');
              handleOpenConsent();
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  // If consent is pending (popup is open), show a waiting message
  if (consentStatus === 'pending') {
    return (
      <div className="card p-lg">
        <h2 className="text-primary mb-md">Microsoft Entra Admin Consent</h2>
        
        <div className="mb-lg">
          <p className="mb-md">
            A popup window has been opened for you to provide admin consent for <strong>{customerName}</strong>.
          </p>
          <p className="mb-md">
            Please complete the consent process in the popup window.
          </p>
          <p className="mb-md">
            Once you've completed the consent process and closed the popup, you'll be automatically returned to this page.
          </p>
          <div className="bg-info p-sm mb-md" style={{ borderRadius: 'var(--border-radius-sm)' }}>
            <p className="text-white">
              <strong>Note:</strong> You must be a Global Administrator in the customer's tenant to provide consent.
            </p>
          </div>
          <div className="flex justify-center mt-lg">
            <div className="spinner"></div>
          </div>
        </div>
        
        <div className="flex justify-between">
          <button 
            className="btn btn-secondary"
            onClick={() => {
              // Close popup if it's open
              if (popupRef.current && !popupRef.current.closed) {
                popupRef.current.close();
              }
              
              // Clean up interval
              if (checkIntervalRef.current) {
                window.clearInterval(checkIntervalRef.current);
                checkIntervalRef.current = null;
              }
              
              // Reset status and close prompt
              setConsentStatus('initial');
              onClose();
            }}
          >
            Cancel
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => {
              // Focus the popup window
              if (popupRef.current && !popupRef.current.closed) {
                popupRef.current.focus();
              }
            }}
          >
            Show Popup
          </button>
        </div>
      </div>
    );
  }
  
  // Default view - prompt to provide consent
  return (
    <div className="card p-lg">
      <h2 className="text-primary mb-md">Microsoft Entra Admin Consent Required</h2>
      
      <div className="mb-lg">
        <p className="mb-md">
          You've successfully added <strong>{customerName}</strong> to the system.
        </p>
        <p className="mb-md">
          To complete the setup, you need to provide admin consent for Microsoft Entra ID integration.
        </p>
        <p className="mb-md">
          This will allow the LMS Manager to access necessary resources in the customer's tenant.
        </p>
        <div className="bg-info p-sm mb-md" style={{ borderRadius: 'var(--border-radius-sm)' }}>
          <p className="text-white">
            <strong>Note:</strong> You must be a Global Administrator in the customer's tenant to provide consent.
          </p>
        </div>
      </div>
      
      <div className="flex justify-between">
        <button 
          className="btn btn-secondary"
          onClick={onClose}
        >
          Skip for Now
        </button>
        <button 
          className="btn btn-primary"
          onClick={handleOpenConsent}
        >
          Provide Admin Consent
        </button>
      </div>
    </div>
  );
};

export default EntraConsentPrompt;
